<template>
  <div class="contact-verification-step">

    <div class="fm-info-banner mb-4">
      <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
      </svg>
      <span>We'll send a verification code to your email and mobile to confirm your contact details.</span>
    </div>

    <div class="otp-grid">
      <div class="otp-grid__item">
        <p class="fm-section-label">
          <svg fill="currentColor" viewBox="0 0 16 16" style="width:12px;height:12px;margin-right:4px;" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
          </svg>
          Email
        </p>
        <EmailVerification ref="emailVerification" :error-container="errorContainerName"/>
      </div>

      <div class="otp-grid__divider"/>

      <div class="otp-grid__item">
        <p class="fm-section-label">
          <svg fill="currentColor" viewBox="0 0 16 16" style="width:12px;height:12px;margin-right:4px;" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h6zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H5z"/>
            <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
          </svg>
          Mobile
        </p>
        <PhoneVerification ref="phoneVerification" :error-container="errorContainerName"/>
      </div>
    </div>

    <slot name="controls" v-bind="{ prev: onPrev, next: onNext }" v-if="canGoNext"/>
  </div>
</template>

<script>
import NewApplicationStepMixin from '@modules/guest/new-applicant/components/mixins/NewApplicationStepMixin';
import FormGroup from '@modules/shared/components/form/FormGroup';
import EmailVerification from './components/EmailVerification';
import store from '@modules/guest/new-applicant/store/store';
import {NEW_APPLICATION} from '@store/constants';
import PhoneVerification from './components/PhoneVerification';

const ERROR_CONTAINER_NAME = 'NewApplicationContactVerificationStepForm';

export default {
  name: 'ContactVerification',
  components: {PhoneVerification, EmailVerification, FormGroup},
  mixins: [NewApplicationStepMixin],
  methods: {
    canGoNext() {
      return this.$refs.emailVerification.original.isEmailVerified
        && !this.$refs.emailVerification.meta.requestChange
        && this.$refs.phoneVerification.original.isPhoneVerified
        && !this.$refs.phoneVerification.meta.requestChange;
    },
    onNext() {
      if (!this.canGoNext()) return;
      store.dispatch(NEW_APPLICATION.ACTIONS.NEXT_STEP);
    },
    onPrev() {
      store.dispatch(NEW_APPLICATION.ACTIONS.PREV_STEP);
    },
  },
  created() {
    this.registerErrorContainer(ERROR_CONTAINER_NAME);
  },
};
</script>

<style lang="scss">
$amber:    #FFB000;
$ink:      #15171C;
$ink-soft: #5B606B;
$line:     #E7E4DA;
$surface:  #FBFAF6;

// ── OTP Grid layout ────────────────────────────────────────
.otp-grid {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 0 2rem;
  align-items: start;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 1.5rem 0;
  }

  &__divider {
    background: $line;
    align-self: stretch;
    @media (max-width: 767px) { display: none; }
  }

  &__item {
    min-width: 0;
  }
}

// ── OTP verify card ────────────────────────────────────────
.otp-verify-card {
  .btn-fm { font-size: 13.5px; padding: 9px 18px; min-height: 42px; }
}

// Verified badge row
.otp-verified {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(34, 197, 94, 0.06);
  border: 1.5px solid rgba(34, 197, 94, 0.25);
  border-radius: 12px;

  &__badge {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #22c55e;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    svg { width: 13px; height: 13px; }
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #16a34a;
  }

  &__value {
    font-size: 14px;
    font-weight: 500;
    color: $ink;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__change { align-self: center; }
}

// OTP code input — large, centered digits
.otp-code-input {
  font-size: 1.5rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.35em !important;
  text-align: center;
}

// Resend countdown hint
.fm-resend-hint {
  font-size: 12.5px;
  color: $ink-soft;
}
</style>
